import { useFormContext } from 'react-hook-form';
import { IncomeReviewFieldsType } from '@brighte/validation';
import { OpenBankingCalculationExplanation } from '@components/OpenBanking/OpenBankingCalculationExplanation';
import {
  SelectionCard,
  SelectionCardActions,
  SelectionCardContent,
  SelectionCardIcon,
  SelectionCardText,
  SelectionCardTitle,
} from '@components/SelectionCard';
import { datadogRum } from '@datadog/browser-rum';
import { css } from '@emotion/css';
import { OpenBankingAccountVerifyStatus, OpenBankingIncomeQuery } from '@graphql/index';
import { Box } from '@spark-web/box';
import { CheckCircleIcon } from '@spark-web/icon';
import { Inline } from '@spark-web/inline';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextLinkButton } from '@spark-web/text-link';
import { useDisclosure } from '@spark-web/utils';
import { formatDollarsWithoutCents } from '@steps/FinancePurchase/utils/getFinanceDetails';
import { INCOME_VERIFICATION, OPEN_BANKING_INCOME_DETAILS, formatDollars } from '@utils';
import { BankAccountsList } from './BankAccountsList';
import EditConfirmation from './EditConfirmation';
import { BankIncomeSources, getUniqueIncomeSources } from './hooks';

interface OpenBankingIncomeReviewSuccessProps {
  openBankingData?: OpenBankingIncomeQuery['openBankingIncome'];
  isOpenBankingConnecting?: boolean;
  onIncomeManualEntry?: () => void;
  onOpenBankingConnect?: () => void;
  bankIncomeSources: BankIncomeSources;
}

export const OpenBankingIncomeReviewSuccess = ({
  openBankingData,
  onIncomeManualEntry,
  onOpenBankingConnect,
  isOpenBankingConnecting,
  bankIncomeSources,
}: OpenBankingIncomeReviewSuccessProps) => {
  const {
    TOTAL_VERIFIED_INCOME,
    INCOME_SYNC_SUCCESS_TITLE,
    ADDITIONNAL_OPEN_BANKING_INCOME,
    ADDITIONNAL_OPEN_BANKING_INCOME_DESCRIPTION,
  } = INCOME_VERIFICATION;

  const { watch, setValue, clearErrors } = useFormContext<IncomeReviewFieldsType>();
  const isIncomeReviewConfirmation = watch('applicant.isIncomeReviewConfirmation');
  const visibleConnectedAccounts = isIncomeReviewConfirmation === false;

  const handleSelectConfirmation = (value: boolean) => {
    if (value) {
      datadogRum.addAction('User agreed that income looks correct');
    } else {
      datadogRum.addAction('User disagreed the income details');
    }
    setValue('applicant.isIncomeReviewConfirmation', value);
    clearErrors('applicant.isIncomeReviewConfirmation');
  };
  const uniqueIncomeSources = getUniqueIncomeSources(openBankingData?.incomeSources ?? []);
  const numberAccounts = uniqueIncomeSources?.length ?? 0;
  const numberVerifiedAccounts =
    uniqueIncomeSources?.filter(account => account?.status === OpenBankingAccountVerifyStatus.VERIFIED)?.length ?? 0;
  const { isOpen, onOpen, onToggle } = useDisclosure();

  return (
    <Stack padding="small" borderRadius="medium" gap="small">
      <SelectionCard border="primary" display="block" paddingY="large">
        <SelectionCardIcon>
          <CheckCircleIcon tone="primary" size="medium" />
        </SelectionCardIcon>
        <SelectionCardContent gap="xlarge" marginTop="large">
          <Text weight="regular" tone="muted">
            {`${numberVerifiedAccounts} of ${numberAccounts} accounts verified`}
          </Text>
          <Text size="large">
            {TOTAL_VERIFIED_INCOME + ' '}
            <span style={{ fontWeight: 'bold' }}>
              {formatDollarsWithoutCents(openBankingData?.totalMonthlyIncome ?? 0)}
            </span>
          </Text>
          <Text>
            <TextLinkButton tone="primaryActive" onClick={onOpen} weight="regular">
              {OPEN_BANKING_INCOME_DETAILS.HOW_WAS_INCOME_ESTIMATED}
            </TextLinkButton>
          </Text>
        </SelectionCardContent>
      </SelectionCard>
      <SelectionCardContent marginTop="medium">
        <EditConfirmation onChange={handleSelectConfirmation} confirmation={isIncomeReviewConfirmation} />
      </SelectionCardContent>
      {visibleConnectedAccounts ? (
        <Box>
          <SelectionCardContent gap="large">
            <Box marginTop="medium">
              <SelectionCardTitle size="large">{INCOME_SYNC_SUCCESS_TITLE}</SelectionCardTitle>
            </Box>
            <BankAccountsList bankIncomeSources={bankIncomeSources} />
            <Stack gap="large" marginTop="medium">
              <Box>
                <SelectionCardTitle size="large">{ADDITIONNAL_OPEN_BANKING_INCOME}</SelectionCardTitle>
              </Box>
              <SelectionCardText size="small">{ADDITIONNAL_OPEN_BANKING_INCOME_DESCRIPTION}</SelectionCardText>
              <Inline gap="medium" alignY="center">
                <SelectionCardActions
                  tone="neutral"
                  onClick={onOpenBankingConnect}
                  loading={isOpenBankingConnecting}
                  disabled={isOpenBankingConnecting}
                >
                  Add another account
                </SelectionCardActions>
                <Text>
                  <TextLinkButton
                    weight="regular"
                    tone="primaryActive"
                    className={css({
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    })}
                    onClick={onIncomeManualEntry}
                  >
                    Enter all income details manually
                  </TextLinkButton>
                </Text>
              </Inline>
            </Stack>
          </SelectionCardContent>
        </Box>
      ) : null}

      <OpenBankingCalculationExplanation isOpen={isOpen} onToggle={onToggle} />
    </Stack>
  );
};
